import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldAlert, Trash2, Search, BadgeCheck, Flag, X, MessageCircle, Heart, Sparkles } from "lucide-react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

const TABS = [
  { key: "users", label: "Users" },
  { key: "reports", label: "Reports" },
];

export default function Admin() {
  const { user: me } = useAuth();
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const loadUsers = async (query = "") => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users${query ? `?q=${encodeURIComponent(query)}` : ""}`);
      setUsers(data);
    } finally { setLoading(false); }
  };

  const loadReports = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reports");
      setReports(data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (tab === "users") loadUsers(q);
    else loadReports();
  }, [tab]);

  useEffect(() => {
    if (tab !== "users") return;
    const t = setTimeout(() => loadUsers(q), 300);
    return () => clearTimeout(t);
  }, [q, tab]);

  const removeUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name} (${u.email}) and ALL their content?`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      setUsers((prev) => prev.filter((x) => x.user_id !== u.user_id));
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to delete user.");
    }
  };

  const deletePost = async (post_id) => {
    if (!window.confirm("Delete this reported post permanently?")) return;
    try {
      await api.delete(`/admin/posts/${post_id}`);
      setReports((prev) => prev.filter((p) => p.post_id !== post_id));
    } catch { alert("Failed."); }
  };

  const dismissReport = async (post_id) => {
    try {
      await api.post(`/admin/reports/${post_id}/dismiss`);
      setReports((prev) => prev.filter((p) => p.post_id !== post_id));
    } catch { alert("Failed."); }
  };

  if (me && !me.is_admin) return <Navigate to="/feed" replace />;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto py-8 px-4 md:px-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 rounded-md bg-red-500/15 border border-red-500/30 flex items-center justify-center">
            <ShieldAlert size={18} className="text-red-300" strokeWidth={2} />
          </div>
          <h1 className="font-heading font-bold text-4xl tracking-tight">Admin</h1>
        </div>
        <p className="text-sm text-white/45 font-mono mb-6">Moderate users and content. Use with care.</p>

        <div className="surface rounded-xl p-2 mb-5 flex items-center gap-1 w-fit" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-4 py-1.5 rounded-md text-xs font-mono uppercase tracking-wider transition flex items-center gap-1.5 ${
                tab === t.key ? "bg-white/10 text-white" : "text-white/45 hover:text-white"
              }`}
            >
              {t.key === "reports" && <Flag size={12} />}
              {t.label}
              {t.key === "reports" && reports.length > 0 && (
                <span className="text-[10px] bg-red-500/20 text-red-300 px-1.5 rounded">{reports.length}</span>
              )}
            </button>
          ))}
        </div>

        {tab === "users" && (
          <>
            <div className="surface rounded-full px-4 py-2.5 mb-5 flex items-center gap-3" data-testid="admin-search">
              <Search size={15} className="text-white/40" strokeWidth={2} />
              <input
                data-testid="admin-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name or email…"
                className="flex-1 bg-transparent focus:outline-none text-sm placeholder:text-white/30"
              />
              <span className="text-[10px] font-mono text-white/30">{users.length}</span>
            </div>

            {loading ? (
              <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="surface rounded-xl h-16 shimmer" />)}</div>
            ) : users.length === 0 ? (
              <div className="surface rounded-xl p-12 text-center text-sm text-white/50">No users found.</div>
            ) : (
              <div className="surface rounded-xl overflow-hidden">
                {users.map((u, i) => (
                  <motion.div
                    key={u.user_id}
                    initial={{ opacity: 0, x: -6 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.02 }}
                    className="flex items-center gap-4 p-4 border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition"
                    data-testid={`admin-user-row-${u.user_id}`}
                  >
                    <Link to={`/profile/${u.user_id}`}>
                      <img
                        src={u.picture || `https://api.dicebear.com/9.x/notionists/svg?seed=${u.user_id}`}
                        alt=""
                        className="w-11 h-11 rounded-md object-cover bg-white/5"
                      />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link to={`/profile/${u.user_id}`} className="font-semibold hover:text-[#00E676] transition truncate">
                          {u.name || "—"}
                        </Link>
                        {u.is_admin && <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-500/15 border border-red-500/30 text-red-300">admin</span>}
                        {u.is_mentor && <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded verified-pill flex items-center gap-0.5"><BadgeCheck size={9} strokeWidth={2.5} /> mentor</span>}
                      </div>
                      <div className="text-[11px] font-mono text-white/40 truncate">{u.email}</div>
                    </div>
                    <div className="hidden sm:flex flex-col items-end text-right">
                      <div className="font-mono text-sm text-[#00E676]">${Math.round(u.total_earnings || 0).toLocaleString()}</div>
                      <div className="text-[10px] font-mono text-white/40 uppercase tracking-wider">{u.posts_count} post{u.posts_count !== 1 && "s"}</div>
                    </div>
                    <button
                      data-testid={`admin-delete-${u.user_id}`}
                      onClick={() => removeUser(u)}
                      disabled={u.is_admin}
                      className="rounded-md px-3 py-2 text-xs font-semibold flex items-center gap-1.5 bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20 transition disabled:opacity-30 disabled:cursor-not-allowed"
                      title={u.is_admin ? "Cannot delete an admin" : "Delete user"}
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "reports" && (
          <>
            {loading ? (
              <div className="space-y-3">{[1, 2].map((i) => <div key={i} className="surface rounded-xl h-32 shimmer" />)}</div>
            ) : reports.length === 0 ? (
              <div className="surface rounded-xl p-12 text-center">
                <Flag size={28} className="text-[#F4E8C1] mx-auto mb-3" strokeWidth={1.5} />
                <div className="font-heading font-bold text-xl mb-1">All clear.</div>
                <div className="text-sm text-white/50">No reported posts right now.</div>
              </div>
            ) : (
              <div className="space-y-3">
                {reports.map((post, i) => (
                  <motion.div
                    key={post.post_id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                    className="surface rounded-xl p-5 border border-red-500/20"
                    data-testid={`report-row-${post.post_id}`}
                  >
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                      <div className="flex items-center gap-2 text-xs font-mono flex-wrap">
                        <span className="px-2 py-1 rounded bg-red-500/15 border border-red-500/30 text-red-300 flex items-center gap-1">
                          <Flag size={11} /> {post.report_count} report{post.report_count !== 1 && "s"}
                        </span>
                        {post.ai_flagged && (
                          <span
                            data-testid={`ai-flag-${post.post_id}`}
                            className="px-2 py-1 rounded bg-[#00E676]/10 border border-[#00E676]/30 text-[#00E676] flex items-center gap-1"
                            title="AI auto-flagged"
                          >
                            <Sparkles size={11} /> AI · {post.ai_confidence ? Math.round(post.ai_confidence * 100) + "%" : "flag"}
                            {post.ai_categories?.length > 0 && (
                              <span className="text-white/50 uppercase tracking-wider">· {post.ai_categories.filter(c => c !== "none").join(", ")}</span>
                            )}
                          </span>
                        )}
                        {post.human_reports > 0 && (
                          <span className="text-white/50">{post.human_reports} human report{post.human_reports !== 1 && "s"}</span>
                        )}
                        <span className="text-white/40">latest {new Date(post.latest_report_at).toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          data-testid={`dismiss-${post.post_id}`}
                          onClick={() => dismissReport(post.post_id)}
                          className="rounded-md px-3 py-1.5 text-xs font-semibold surface-elev hover:bg-white/10 transition flex items-center gap-1.5"
                        >
                          <X size={12} /> Dismiss
                        </button>
                        <button
                          data-testid={`delete-reported-${post.post_id}`}
                          onClick={() => deletePost(post.post_id)}
                          className="rounded-md px-3 py-1.5 text-xs font-semibold bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20 transition flex items-center gap-1.5"
                        >
                          <Trash2 size={12} /> Delete post
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mb-2 text-sm">
                      <Link to={`/profile/${post.author?.user_id}`}>
                        <img
                          src={post.author?.picture || `https://api.dicebear.com/9.x/notionists/svg?seed=${post.author?.user_id}`}
                          alt=""
                          className="w-8 h-8 rounded-md object-cover bg-white/5"
                        />
                      </Link>
                      <Link to={`/profile/${post.author?.user_id}`} className="font-semibold hover:text-[#00E676] transition">
                        {post.author?.name || "Unknown"}
                      </Link>
                      <span className="text-xs text-white/40 font-mono">· {new Date(post.created_at).toLocaleDateString()}</span>
                      {post.niche && <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded bg-white/5">{post.niche}</span>}
                    </div>

                    {post.content && (
                      <p className="text-sm text-white/80 mb-3 whitespace-pre-wrap leading-relaxed line-clamp-4">{post.content}</p>
                    )}

                    {(post.image_url || post.proof_image_url) && !post.video_url && (
                      <img src={post.proof_image_url || post.image_url} alt="" className="rounded-md max-h-48 mb-3 object-cover border border-white/5" />
                    )}
                    {post.video_url && (
                      <video src={post.video_url} className="rounded-md max-h-48 mb-3 bg-black" controls />
                    )}

                    {post.post_type === "income_proof" && post.amount && (
                      <div className="mb-3 text-sm font-mono text-[#00E676]">${Number(post.amount).toLocaleString()} <span className="text-white/40">income proof</span></div>
                    )}

                    {post.report_reasons.length > 0 && (
                      <div className="surface-elev rounded-md p-3 mt-2">
                        <div className="text-[10px] font-mono uppercase tracking-wider text-white/40 mb-1.5">Reasons given</div>
                        <ul className="space-y-1">
                          {post.report_reasons.map((r, idx) => (
                            <li key={idx} className="text-xs text-white/75">• {r || <span className="text-white/30">(no reason)</span>}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="flex items-center gap-3 mt-3 text-[11px] font-mono text-white/40">
                      <span className="flex items-center gap-1"><Heart size={11} /> {post.likes_count}</span>
                      <span className="flex items-center gap-1"><MessageCircle size={11} /> {post.comments_count}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
